import { z } from 'zod';
import type { TakeAwayStoreToken } from '@sleed/bizcommerce-api';

/**
 * Takeaway store / pickup point (Σημείο Παραλαβής) edit form. Mirrors `TakeAwayStoreToken` and the
 * legacy admin popup (Core/client/admin/src/pages/Orders/TakeAwayStores/index.js): nm required ≤200,
 * cid (country) required, rid optional FK, address block (adl/adl2/ar/ct/pc/lat/lng), contact
 * (mgr/eml/tel), logistics (frt/tprc/tds), hours (ohs/ohe + work_hours HTML), dsc HTML, erpsc, wcd (warehouse code ≤50), img1-4.
 * `nm` is multilanguage server-side (writes go to the active Accept-Language, default `el`). Image bytes
 * upload out-of-band (img{1..4}File → binaryRepoImagesApi.uploadTakeAwayStore, needs a saved id).
 * `ohj` (open-hours JSON) and `drty` (geocode-dirty flag) are preserved as-is (not surfaced in the UI).
 */
const nullableStr = z.string().nullable();
const nullableNumber = z.preprocess(v => (v === '' || v === null || v === undefined ? null : Number(v)), z.number().nullable());

export const takeawayStoreSchema = z
	.object({
		id: z.number().optional(),
		nm: z.string().trim().min(1, 'validation.required').max(200, 'validation.tooLong'),
		cid: z.number().nullable(),
		rid: z.number().nullable(),
		mgr: z.string().trim().max(100, 'validation.tooLong').nullable(),
		eml: z.string().trim().max(255, 'validation.tooLong').nullable(),
		tel: z.string().trim().max(20, 'validation.tooLong').nullable(),
		tds: nullableNumber,
		frt: nullableNumber,
		tprc: nullableNumber,
		erpsc: z.string().trim().max(50, 'validation.tooLong').nullable(),
		wcd: z.string().trim().max(50, 'validation.tooLong').nullable(),
		adl: z.string().trim().max(200, 'validation.tooLong').nullable(),
		adl2: z.string().trim().max(200, 'validation.tooLong').nullable(),
		ar: z.string().trim().max(50, 'validation.tooLong').nullable(),
		ct: z.string().trim().max(50, 'validation.tooLong').nullable(),
		pc: z.string().trim().max(20, 'validation.tooLong').nullable(),
		lat: nullableNumber,
		lng: nullableNumber,
		ohs: nullableNumber,
		ohe: nullableNumber,
		ohj: nullableStr,
		work_hours: nullableStr,
		dsc: nullableStr,
		iv: z.boolean(),
		si: z.number({ message: 'validation.required' }).int(),
		drty: z.boolean(),
		img1: nullableStr,
		imgdel1: z.boolean(),
		img1File: z.instanceof(File).nullable(),
		img2: nullableStr,
		imgdel2: z.boolean(),
		img2File: z.instanceof(File).nullable(),
		img3: nullableStr,
		imgdel3: z.boolean(),
		img3File: z.instanceof(File).nullable(),
		img4: nullableStr,
		imgdel4: z.boolean(),
		img4File: z.instanceof(File).nullable()
	})
	.superRefine((val, ctx) => {
		// Country (cid) is required — mirrors the legacy grid's RequiredRule on the country column.
		if (val.cid == null) ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'validation.required', path: ['cid'] });
	});

export type TakeawayStoreFormValues = z.infer<typeof takeawayStoreSchema>;

export const EMPTY_TAKEAWAY_STORE: TakeawayStoreFormValues = {
	nm: '',
	cid: null,
	rid: null,
	mgr: null,
	eml: null,
	tel: null,
	tds: null,
	frt: null,
	tprc: null,
	erpsc: null,
	wcd: null,
	adl: null,
	adl2: null,
	ar: null,
	ct: null,
	pc: null,
	lat: null,
	lng: null,
	ohs: null,
	ohe: null,
	ohj: null,
	work_hours: null,
	dsc: null,
	iv: true,
	si: 0,
	drty: false,
	img1: null,
	imgdel1: false,
	img1File: null,
	img2: null,
	imgdel2: false,
	img2File: null,
	img3: null,
	imgdel3: false,
	img3File: null,
	img4: null,
	imgdel4: false,
	img4File: null
};

export function tokenToForm(token: TakeAwayStoreToken): TakeawayStoreFormValues {
	return {
		id: token.id,
		nm: token.nm ?? '',
		cid: token.cid ?? null,
		rid: token.rid ?? null,
		mgr: token.mgr ?? null,
		eml: token.eml ?? null,
		tel: token.tel ?? null,
		tds: token.tds ?? null,
		frt: token.frt ?? null,
		tprc: token.tprc ?? null,
		erpsc: token.erpsc ?? null,
		wcd: token.wcd ?? null,
		adl: token.adl ?? null,
		adl2: token.adl2 ?? null,
		ar: token.ar ?? null,
		ct: token.ct ?? null,
		pc: token.pc ?? null,
		lat: token.lat ?? null,
		lng: token.lng ?? null,
		ohs: token.ohs ?? null,
		ohe: token.ohe ?? null,
		ohj: token.ohj ?? null,
		work_hours: token.work_hours ?? null,
		dsc: token.dsc ?? null,
		iv: token.iv ?? false,
		si: token.si ?? 0,
		drty: token.drty ?? false,
		img1: token.img1 ?? null,
		imgdel1: false,
		img1File: null,
		img2: token.img2 ?? null,
		imgdel2: false,
		img2File: null,
		img3: token.img3 ?? null,
		imgdel3: false,
		img3File: null,
		img4: token.img4 ?? null,
		imgdel4: false,
		img4File: null
	};
}

export function formToToken(form: TakeawayStoreFormValues): TakeAwayStoreToken {
	return {
		id: form.id,
		nm: form.nm.trim(),
		cid: form.cid,
		rid: form.rid,
		mgr: form.mgr,
		eml: form.eml,
		tel: form.tel,
		tds: form.tds,
		frt: form.frt,
		tprc: form.tprc,
		erpsc: form.erpsc,
		wcd: form.wcd?.trim() || null,
		adl: form.adl,
		adl2: form.adl2,
		ar: form.ar,
		ct: form.ct,
		pc: form.pc,
		lat: form.lat,
		lng: form.lng,
		ohs: form.ohs,
		ohe: form.ohe,
		ohj: form.ohj,
		work_hours: form.work_hours,
		dsc: form.dsc,
		iv: form.iv,
		si: form.si,
		drty: form.drty,
		img1: form.img1,
		imgdel1: form.imgdel1,
		img2: form.img2,
		imgdel2: form.imgdel2,
		img3: form.img3,
		imgdel3: form.imgdel3,
		img4: form.img4,
		imgdel4: form.imgdel4
	};
}
